import {
  AccountTransaction,
  AggregateStoreSalesReport,
  Big,
  canonicalTransactionCategory,
  Debt,
  EntityTypes,
  formatCurrency,
  IntervalAggregateReport,
  InventoryChange,
  InventoryChangeReason,
  PurchaseOrder,
  PurchaseOrderPayment,
  SalesReportInterval,
  StoreInventoryValue,
  Supplier,
  SupplierValuation,
  TransactionCategory,
  TransactionFlow,
} from '@bofrak-backend/shared';
import { ChevronRightIcon, RepeatIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  HStack,
  Skeleton,
  Spacer,
  Spinner,
  Switch,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  differenceInCalendarDays,
  differenceInHours,
  eachDayOfInterval,
  eachHourOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  format,
  parseISO,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ReactElement, useMemo, useState } from 'react';
import { useQueries, useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiAdapter } from '../../api/backend';
import { merchantAtom, storeAtom } from '../../recoil/atoms';
import { ClientDebtsDrawer } from './ClientDebtsDrawer';
import { SectionHeader } from './MetricCard';
import {
  aggregateSnapshot,
  computeSamplePoints,
  MoneyValueDrawer,
} from './MoneyValueDrawer';
import { SupplierDebtsDrawer } from './SupplierDebtsDrawer';
import { useSharedUiTranslation, type SharedUiT } from '../../../i18n';
import type { DashboardStatus } from '../../hooks/use-dashboard-queries';
import {
  combineStatus,
  toQueryStatus,
  type QueryStatus,
} from '../../utils/query-status';

/* ── Chart palette (CVD-validated pairs, matches app conventions) ── */
const COLORS = {
  blue: '#3182CE',
  orange: '#DD6B20',
  green: '#38A169',
  red: '#E53E3E',
  purple: '#805AD5',
  cyan: '#0987A0',
};

const TICK = { fontSize: 9 };
const TOOLTIP_STYLE = { fontSize: 11 };

/** Compact number for Y-axis ticks (full currency stays in tooltips). */
function compactNumber(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${Math.round(v / 1_000)}k`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return `${Math.round(v)}`;
}

/* ── Time bucketing ── */

type TrendInterval = 'hourly' | 'daily' | 'weekly' | 'monthly';

const INTERVAL_TO_API: Record<TrendInterval, SalesReportInterval> = {
  hourly: SalesReportInterval.HOURLY,
  daily: SalesReportInterval.DAILY,
  weekly: SalesReportInterval.WEEKLY,
  monthly: SalesReportInterval.MONTHLY,
};

const INTERVAL_LABEL_FORMAT: Record<TrendInterval, string> = {
  hourly: 'dd.MM HH:mm',
  daily: 'dd.MM',
  weekly: 'dd.MM',
  monthly: 'MMM yy',
};

function chooseInterval(start: Date, end: Date): TrendInterval {
  if (differenceInHours(end, start) <= 48) return 'hourly';
  const days = differenceInCalendarDays(end, start);
  if (days <= 124) return 'daily';
  if (days <= 550) return 'weekly';
  return 'monthly';
}

function bucketStart(date: Date, interval: TrendInterval): Date {
  switch (interval) {
    case 'hourly':
      return startOfHour(date);
    case 'daily':
      return startOfDay(date);
    case 'weekly':
      return startOfWeek(date, { weekStartsOn: 1 });
    case 'monthly':
      return startOfMonth(date);
  }
}

/** All bucket start times covering [start, end], so charts have no gaps. */
function bucketsForRange(
  start: Date,
  end: Date,
  interval: TrendInterval,
): Date[] {
  const opts = { start: bucketStart(start, interval), end };
  switch (interval) {
    case 'hourly':
      return eachHourOfInterval(opts);
    case 'daily':
      return eachDayOfInterval(opts);
    case 'weekly':
      return eachWeekOfInterval(opts, { weekStartsOn: 1 });
    case 'monthly':
      return eachMonthOfInterval(opts);
  }
}

/** Sum item values into the buckets they fall in. Returns bucketTime → sum. */
function bucketSums<T>(
  items: T[],
  getDate: (item: T) => Date,
  getValue: (item: T) => number,
  interval: TrendInterval,
): Map<number, number> {
  const sums = new Map<number, Big>();
  for (const item of items) {
    const key = bucketStart(getDate(item), interval).getTime();
    sums.set(key, (sums.get(key) ?? new Big(0)).plus(getValue(item) || 0));
  }
  const result = new Map<number, number>();
  for (const [k, v] of sums) result.set(k, v.toNumber());
  return result;
}

/** Turn rows into running totals for the given keys (progress view). */
function accumulate<T extends Record<string, unknown>>(
  rows: T[],
  keys: string[],
): T[] {
  const totals = new Map<string, Big>();
  return rows.map((row) => {
    const next: Record<string, unknown> = { ...row };
    for (const key of keys) {
      const total = (totals.get(key) ?? new Big(0)).plus(
        (row[key] as number) || 0,
      );
      totals.set(key, total);
      next[key] = total.toNumber();
    }
    return next as T;
  });
}

/* ── Shared chart chrome ── */

function ChartCard({
  title,
  status,
  isEmpty,
  height = 210,
  onOpenDetails,
  toggle,
  children,
}: {
  title: string;
  /** The chart's request state; a failure shows Retry, not "No data". */
  status?: QueryStatus;
  isEmpty?: boolean;
  height?: number;
  onOpenDetails?: () => void;
  toggle?: ReactElement;
  children: ReactElement;
}) {
  const { t } = useSharedUiTranslation();
  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      px={1.5}
      py={2}
      w="full">
      <HStack
        mb={1}
        px={1}
        spacing={1}
        cursor={onOpenDetails ? 'pointer' : undefined}
        _hover={onOpenDetails ? { color: 'blue.600' } : undefined}
        onClick={onOpenDetails}>
        <Text fontSize="2xs" fontWeight="bold" color="gray.700">
          {title}
        </Text>
        {toggle && (
          <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
            {toggle}
          </HStack>
        )}
        {onOpenDetails && (
          <>
            <Spacer />
            <Text fontSize="2xs" color="gray.400">
              {t('Details')}
            </Text>
            <ChevronRightIcon boxSize={3} color="gray.400" />
          </>
        )}
      </HStack>
      {status?.isError ? (
        <VStack py={6} spacing={2}>
          <Text fontSize="xs" color="red.500">
            {t('Failed to load')}
          </Text>
          <Button
            size="xs"
            colorScheme="red"
            variant="outline"
            leftIcon={<RepeatIcon />}
            onClick={status.onRetry}>
            {t('Retry')}
          </Button>
        </VStack>
      ) : status?.isLoading ? (
        <Skeleton height={`${height}px`} borderRadius="md" />
      ) : isEmpty ? (
        <Text fontSize="xs" color="gray.500" textAlign="center" py={8}>
          {t('No data for this period.')}
        </Text>
      ) : (
        <Box height={`${height}px`} w="full">
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}

const moneyTooltipFormatter = (v: number, name: string): [string, string] => [
  formatCurrency(v, true),
  name,
];

/* ── Category labels for the breakdown chart (matches ValuationSlide) ── */
const getCategoryLabels = (
  t: SharedUiT,
): Record<TransactionCategory, string> => ({
  [TransactionCategory.SALES_RECEIPT]: t('Sales Receipts'),
  [TransactionCategory.SALES_REFUND]: t('Sales Refunds'),
  [TransactionCategory.DEBT_PAYMENT]: t('Debt Payments'),
  [TransactionCategory.LOAN_PAYMENT]: t('Loan Payments'),
  [TransactionCategory.LOAN_GIVEN]: t('Loans Given'),
  [TransactionCategory.TRANSFER_IN]: t('Transfers In'),
  [TransactionCategory.TRANSFER_OUT]: t('Transfers Out'),
  [TransactionCategory.INVESTMENT]: t('Investments'),
  [TransactionCategory.DIVIDEND]: t('Profit Payouts'),
  [TransactionCategory.SUPPLIER_PAYMENT]: t('Supplier Payments'),
  [TransactionCategory.SUPPLIER_REFUND]: t('Supplier Refunds'),
  [TransactionCategory.EXPENSE]: t('Expenses'),
  [TransactionCategory.CORRECTION]: t('Corrections'),
  [TransactionCategory.OPENING_CORRECTION]: t('Opening Corrections'),
  [TransactionCategory.CLOSING_CORRECTION]: t('Closing Corrections'),
  [TransactionCategory.DEBT_PAYMENT_REVERSAL]: t('Debt Payment Reversals'),
  [TransactionCategory.LOAN_PAYMENT_REVERSAL]: t('Loan Payment Reversals'),
  [TransactionCategory.LOAN_GIVEN_REVERSAL]: t('Loan Given Reversals'),
  [TransactionCategory.EXPENSE_REVERSAL]: t('Expense Reversals'),
  [TransactionCategory.DIVIDEND_REVERSAL]: t('Profit Payout Reversals'),
  [TransactionCategory.INVESTMENT_REVERSAL]: t('Investment Reversals'),
});

/** Reasons excluded from count/correction impact (mirrors dashboard). */
const GAIN_LOSS_EXCLUDED = new Set<InventoryChangeReason>([
  InventoryChangeReason.SALE,
  InventoryChangeReason.REFUND,
  InventoryChangeReason.PURCHASE_ORDER,
]);

interface TrendsSlideProps {
  dateRange: { start: Date | null; end: Date | null };
  /** Per-request state: each chart waits only for its own data. */
  status: DashboardStatus;
  accountTransactions: AccountTransaction[];
  inventoryValues: StoreInventoryValue[];
  allInventoryChanges: InventoryChange[];
  debts: Debt[];
  purchaseOrders: PurchaseOrder[];
  transactionBreakdown: Record<TransactionCategory, number>;
  /* Detail-drawer data (drawers contain their own history + graph tabs) */
  moneyStart: number;
  moneyEnd: number;
  allStoreDebts: Debt[];
  clientDebtsStart: number;
  clientDebtsEnd: number;
  /** Ledger-based debt payments split (money vs refund settlements). */
  debtPaymentsPeriod?: {
    money: number;
    refundSettled: number;
    total: number;
  } | null;
  supplierValuation?: SupplierValuation;
  purchaseOrderPayments?: PurchaseOrderPayment[];
  suppliersMap?: Map<string, Supplier>;
}

export function TrendsSlide({
  dateRange,
  status,
  accountTransactions,
  inventoryValues,
  allInventoryChanges,
  debts,
  purchaseOrders,
  transactionBreakdown,
  moneyStart,
  moneyEnd,
  allStoreDebts,
  clientDebtsStart,
  clientDebtsEnd,
  debtPaymentsPeriod = null,
  supplierValuation,
  purchaseOrderPayments,
  suppliersMap,
}: TrendsSlideProps) {
  const { t } = useSharedUiTranslation();
  const merchant = useRecoilValue(merchantAtom);
  const store = useRecoilValue(storeAtom);
  const [cumulative, setCumulative] = useState(false);
  const [chartTypes, setChartTypes] = useState<Record<string, 'bar' | 'line'>>({
    sales: 'bar',
    expenses: 'bar',
    receipts: 'bar',
    money: 'bar',
    debts: 'bar',
    suppliers: 'bar',
    inventory: 'line',
  });

  const toggleChartType = (key: string) =>
    setChartTypes((prev) => ({
      ...prev,
      [key]: prev[key] === 'bar' ? 'line' : 'bar',
    }));

  const moneyDrawer = useDisclosure();
  const clientDebtsDrawer = useDisclosure();
  const supplierDrawer = useDisclosure();

  const hasRange = !!dateRange.start && !!dateRange.end;
  const interval: TrendInterval = hasRange
    ? chooseInterval(dateRange.start!, dateRange.end!)
    : 'daily';
  const labelFormat = INTERVAL_LABEL_FORMAT[interval];

  const buckets = useMemo(
    () =>
      hasRange
        ? bucketsForRange(dateRange.start!, dateRange.end!, interval)
        : [],
    [hasRange, dateRange.start, dateRange.end, interval],
  );

  /* ── Sales report intervals (net sales, gross profit, receipt counts) ── */
  const salesIntervalsQuery = useQuery(
    ['salesReportIntervals', dateRange, store?.id, merchant?.id, interval],
    () =>
      apiAdapter.getSalesReportByInterval({
        dto: {
          entityType: EntityTypes.STORE,
          entityId: store!.id,
          from: dateRange.start!.toISOString(),
          to: dateRange.end!.toISOString(),
          interval: INTERVAL_TO_API[interval],
          is_cumulative: false,
        },
        store_id: store!.id,
        merchant_id: merchant!.id,
      }) as Promise<IntervalAggregateReport<AggregateStoreSalesReport>[]>,
    { enabled: hasRange && !!store?.id && !!merchant?.id },
  );
  const salesIntervals = salesIntervalsQuery.data;
  const salesStatus = toQueryStatus(salesIntervalsQuery);

  /* ── Available money timeline (same sampled snapshots as MoneyValueDrawer) ── */
  const samplePoints = useMemo(
    () =>
      hasRange ? computeSamplePoints(dateRange.start!, dateRange.end!) : [],
    [hasRange, dateRange.start, dateRange.end],
  );

  const snapshotQueries = useQueries(
    samplePoints.map((point) => ({
      queryKey: [
        'storeSnapshotsTimeline',
        merchant?.id,
        store?.id,
        point.toISOString(),
      ],
      queryFn: () =>
        apiAdapter.getStoreSnapshotsAtTime({
          merchant_id: merchant!.id,
          store_id: store!.id,
          at_time: point.toISOString(),
        }),
      enabled: !!merchant?.id && !!store?.id,
      staleTime: 5 * 60 * 1000,
    })),
  );
  const isLoadingMoney = snapshotQueries.some((q) => q.isLoading);
  const moneyStatus = combineStatus(...snapshotQueries.map(toQueryStatus));
  const moneyLoadedCount = snapshotQueries.filter((q) => q.isSuccess).length;

  const moneyData = useMemo(
    () =>
      snapshotQueries
        .filter((q) => !!q.data)
        .map((q) => aggregateSnapshot(q.data!))
        .map((p) => ({
          label: format(p.time, 'dd.MM HH:mm'),
          money: p.total,
        })),
    [snapshotQueries],
  );

  /* ── Transaction helpers ── */

  /** Signed magnitude per bucket for a set of canonical categories.
   *  Direction comes from `flow` (like MoneyValueDrawer): INFLOW adds,
   *  OUTFLOW subtracts — flip with `positiveFlow` for outflow metrics. */
  const categoryBucketSums = (
    categories: Set<TransactionCategory>,
    positiveFlow: TransactionFlow,
  ) =>
    bucketSums(
      accountTransactions.filter((tx) =>
        categories.has(canonicalTransactionCategory(tx.category)),
      ),
      (tx) => new Date(tx.created_at),
      (tx) =>
        tx.flow === positiveFlow ? Math.abs(tx.amount) : -Math.abs(tx.amount),
      interval,
    );

  /* ── Sales performance rows (intervals API + expenses from transactions) ── */
  const salesRows = useMemo(() => {
    if (!salesIntervals?.length) return [];
    const expensesByBucket = categoryBucketSums(
      new Set([TransactionCategory.EXPENSE]),
      TransactionFlow.OUTFLOW,
    );
    return salesIntervals.map((r) => {
      const start = parseISO(r.interval_start);
      const report = r.aggregated_report;
      const expenses =
        expensesByBucket.get(bucketStart(start, interval).getTime()) ?? 0;
      const grossProfit = report.gross_profit || 0;
      return {
        label: format(start, labelFormat),
        netSales: report.net_sales || 0,
        grossProfit,
        expenses,
        netProfit: new Big(grossProfit).minus(expenses).toNumber(),
        saleCount: report.sale_receipt_count || 0,
        refundCount: report.refund_receipt_count || 0,
        avgSale:
          report.sale_receipt_count > 0
            ? new Big(report.net_sales || 0)
                .div(report.sale_receipt_count)
                .toNumber()
            : 0,
      };
    });
  }, [salesIntervals, accountTransactions, interval, labelFormat]);

  /* ── Bucketed rows from local data (transactions, debts, POs, changes) ── */
  const bucketRows = useMemo(() => {
    const inflows = bucketSums(
      accountTransactions.filter((tx) => tx.flow === TransactionFlow.INFLOW),
      (tx) => new Date(tx.created_at),
      (tx) => Math.abs(tx.amount),
      interval,
    );
    const outflows = bucketSums(
      accountTransactions.filter((tx) => tx.flow === TransactionFlow.OUTFLOW),
      (tx) => new Date(tx.created_at),
      (tx) => Math.abs(tx.amount),
      interval,
    );
    const gainLoss = bucketSums(
      allInventoryChanges.filter((c) => !GAIN_LOSS_EXCLUDED.has(c.reason)),
      (c) => new Date(c.created_at),
      (c) => new Big(c.change_quantity).times(c.cost).toNumber(),
      interval,
    );
    const newDebts = bucketSums(
      debts,
      (d) => new Date(d.created_at),
      (d) => d.principal_amount || 0,
      interval,
    );
    const collected = categoryBucketSums(
      new Set([
        TransactionCategory.DEBT_PAYMENT,
        TransactionCategory.LOAN_PAYMENT,
      ]),
      TransactionFlow.INFLOW,
    );
    const purchases = bucketSums(
      purchaseOrders.filter((po) => !po.is_draft && !po.cancelled_at),
      (po) => new Date(po.purchase_date),
      (po) => po.total_cost || 0,
      interval,
    );
    const supplierPaid = categoryBucketSums(
      new Set([TransactionCategory.SUPPLIER_PAYMENT]),
      TransactionFlow.OUTFLOW,
    );

    return buckets.map((b) => {
      const key = b.getTime();
      return {
        label: format(b, labelFormat),
        moneyIn: inflows.get(key) ?? 0,
        moneyOut: outflows.get(key) ?? 0,
        gainLoss: gainLoss.get(key) ?? 0,
        newDebts: newDebts.get(key) ?? 0,
        collected: collected.get(key) ?? 0,
        purchases: purchases.get(key) ?? 0,
        supplierPaid: supplierPaid.get(key) ?? 0,
      };
    });
  }, [
    accountTransactions,
    allInventoryChanges,
    debts,
    purchaseOrders,
    buckets,
    interval,
    labelFormat,
  ]);

  /* ── Inventory value snapshots (already a timeline) ── */
  const inventoryData = useMemo(
    () =>
      inventoryValues.map((v) => ({
        label: format(new Date(v.created_at), 'dd.MM HH:mm'),
        inventoryValue: v.inventory_value || 0,
        retailValue: v.retail_value || 0,
      })),
    [inventoryValues],
  );

  /* ── Transaction category totals (horizontal bars) ── */
  const categoryData = useMemo(
    () =>
      Object.entries(transactionBreakdown)
        .filter(([, value]) => value !== 0)
        .map(([category, value]) => ({
          label:
            getCategoryLabels(t)[category as TransactionCategory] || category,
          value,
        }))
        .sort((a, b) => Math.abs(b.value) - Math.abs(a.value)),
    [transactionBreakdown, t],
  );

  /* ── Cumulative (progress) view ── */
  const salesView = useMemo(
    () =>
      cumulative
        ? accumulate(salesRows, [
            'netSales',
            'grossProfit',
            'expenses',
            'netProfit',
            'saleCount',
            'refundCount',
          ])
        : salesRows,
    [salesRows, cumulative],
  );
  const bucketView = useMemo(
    () =>
      cumulative
        ? accumulate(bucketRows, [
            'moneyIn',
            'moneyOut',
            'gainLoss',
            'newDebts',
            'collected',
            'purchases',
            'supplierPaid',
          ])
        : bucketRows,
    [bucketRows, cumulative],
  );

  const hasTransactions = bucketRows.some(
    (r) => r.moneyIn !== 0 || r.moneyOut !== 0,
  );
  const hasGainLoss = bucketRows.some((r) => r.gainLoss !== 0);
  const hasDebtActivity = bucketRows.some(
    (r) => r.newDebts !== 0 || r.collected !== 0,
  );
  const hasSupplierActivity = bucketRows.some(
    (r) => r.purchases !== 0 || r.supplierPaid !== 0,
  );

  const intervalLabel: Record<TrendInterval, string> = {
    hourly: t('Per hour'),
    daily: t('Per day'),
    weekly: t('Per week'),
    monthly: t('Per month'),
  };

  return (
    <VStack spacing={2} align="stretch" w="full" px={0.5}>
      {/* View controls */}
      <HStack justify="space-between" px={1}>
        <Text fontSize="2xs" color="gray.500">
          {intervalLabel[interval]}
        </Text>
        <HStack spacing={1.5}>
          <Text fontSize="2xs" color="gray.600">
            {t('Cumulative')}
          </Text>
          <Switch
            size="sm"
            colorScheme="blue"
            isChecked={cumulative}
            onChange={(e) => setCumulative(e.target.checked)}
          />
        </HStack>
      </HStack>

      {/* ── Sales Performance ── */}
      <SectionHeader color="blue.400" label={t('Sales Performance')} />

      <ChartCard
        title={t('Net Sales & Gross Profit')}
        status={salesStatus}
        isEmpty={salesView.length === 0}
        toggle={
          <HStack spacing={1}>
            <Text fontSize="2xs" color="gray.500">
              {chartTypes.sales === 'bar' ? t('Bar') : t('Line')}
            </Text>
            <Switch
              size="sm"
              colorScheme="blue"
              isChecked={chartTypes.sales === 'line'}
              onChange={() => toggleChartType('sales')}
            />
          </HStack>
        }>
        {chartTypes.sales === 'bar' ? (
          <BarChart data={salesView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} tickFormatter={compactNumber} width={42} />
            <Tooltip
              formatter={moneyTooltipFormatter}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar
              dataKey="netSales"
              name={t('Net Sales')}
              fill={COLORS.blue}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
            <Bar
              dataKey="grossProfit"
              name={t('Gross Profit')}
              fill={COLORS.orange}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
          </BarChart>
        ) : (
          <LineChart data={salesView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} tickFormatter={compactNumber} width={42} />
            <Tooltip
              formatter={moneyTooltipFormatter}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line
              type="monotone"
              dataKey="netSales"
              name={t('Net Sales')}
              stroke={COLORS.blue}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="grossProfit"
              name={t('Gross Profit')}
              stroke={COLORS.orange}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        )}
      </ChartCard>

      <ChartCard
        title={t('Expenses & Net Profit')}
        status={combineStatus(salesStatus, status.accountTransactions)}
        isEmpty={salesView.length === 0}
        toggle={
          <HStack spacing={1}>
            <Text fontSize="2xs" color="gray.500">
              {chartTypes.expenses === 'bar' ? t('Bar') : t('Line')}
            </Text>
            <Switch
              size="sm"
              colorScheme="blue"
              isChecked={chartTypes.expenses === 'line'}
              onChange={() => toggleChartType('expenses')}
            />
          </HStack>
        }>
        {chartTypes.expenses === 'bar' ? (
          <BarChart data={salesView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} tickFormatter={compactNumber} width={42} />
            <Tooltip
              formatter={moneyTooltipFormatter}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <ReferenceLine y={0} stroke="#A0AEC0" />
            <Bar
              dataKey="expenses"
              name={t('Expenses')}
              fill={COLORS.red}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
            <Bar
              dataKey="netProfit"
              name={t('Net Profit')}
              fill={COLORS.green}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
          </BarChart>
        ) : (
          <LineChart data={salesView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} tickFormatter={compactNumber} width={42} />
            <Tooltip
              formatter={moneyTooltipFormatter}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <ReferenceLine y={0} stroke="#A0AEC0" />
            <Line
              type="monotone"
              dataKey="expenses"
              name={t('Expenses')}
              stroke={COLORS.red}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="netProfit"
              name={t('Net Profit')}
              stroke={COLORS.green}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        )}
      </ChartCard>

      <ChartCard
        title={t('Receipts: Sales vs Refunds')}
        status={salesStatus}
        isEmpty={salesView.length === 0}
        toggle={
          <HStack spacing={1}>
            <Text fontSize="2xs" color="gray.500">
              {chartTypes.receipts === 'bar' ? t('Bar') : t('Line')}
            </Text>
            <Switch
              size="sm"
              colorScheme="blue"
              isChecked={chartTypes.receipts === 'line'}
              onChange={() => toggleChartType('receipts')}
            />
          </HStack>
        }>
        {chartTypes.receipts === 'bar' ? (
          <BarChart data={salesView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} allowDecimals={false} width={30} />
            <Tooltip labelStyle={TOOLTIP_STYLE} contentStyle={TOOLTIP_STYLE} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar
              dataKey="saleCount"
              name={t('Sale Receipts')}
              fill={COLORS.blue}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
            <Bar
              dataKey="refundCount"
              name={t('Refund Receipts')}
              fill={COLORS.red}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
          </BarChart>
        ) : (
          <LineChart data={salesView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} allowDecimals={false} width={30} />
            <Tooltip labelStyle={TOOLTIP_STYLE} contentStyle={TOOLTIP_STYLE} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line
              type="monotone"
              dataKey="saleCount"
              name={t('Sale Receipts')}
              stroke={COLORS.blue}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="refundCount"
              name={t('Refund Receipts')}
              stroke={COLORS.red}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        )}
      </ChartCard>

      {!cumulative && (
        <ChartCard
          title={t('Average Sale per Receipt')}
          status={salesStatus}
          isEmpty={salesView.length === 0}
          height={170}>
          <LineChart data={salesView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} tickFormatter={compactNumber} width={42} />
            <Tooltip
              formatter={(v: number) => [
                formatCurrency(v, true),
                t('Avg Sale'),
              ]}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Line
              type="monotone"
              dataKey="avgSale"
              stroke={COLORS.blue}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ChartCard>
      )}

      {/* ── Inventory ── */}
      <SectionHeader color="teal.400" label={t('Inventory')} />

      <ChartCard
        title={t('Inventory Value (Cost vs Retail)')}
        status={status.inventoryValues}
        isEmpty={inventoryData.length === 0}
        toggle={
          <HStack spacing={1}>
            <Text fontSize="2xs" color="gray.500">
              {chartTypes.inventory === 'bar' ? t('Bar') : t('Line')}
            </Text>
            <Switch
              size="sm"
              colorScheme="blue"
              isChecked={chartTypes.inventory === 'line'}
              onChange={() => toggleChartType('inventory')}
            />
          </HStack>
        }>
        {chartTypes.inventory === 'bar' ? (
          <BarChart data={inventoryData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis
              tick={TICK}
              tickFormatter={compactNumber}
              width={42}
              domain={['auto', 'auto']}
            />
            <Tooltip
              formatter={moneyTooltipFormatter}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar
              dataKey="inventoryValue"
              name={t('Cost Value')}
              fill={COLORS.cyan}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
            <Bar
              dataKey="retailValue"
              name={t('Retail Value')}
              fill={COLORS.orange}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
          </BarChart>
        ) : (
          <LineChart data={inventoryData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis
              tick={TICK}
              tickFormatter={compactNumber}
              width={42}
              domain={['auto', 'auto']}
            />
            <Tooltip
              formatter={moneyTooltipFormatter}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line
              type="monotone"
              dataKey="inventoryValue"
              name={t('Cost Value')}
              stroke={COLORS.cyan}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="retailValue"
              name={t('Retail Value')}
              stroke={COLORS.orange}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        )}
      </ChartCard>

      <ChartCard
        title={t('Inventory Count/Correction Impact')}
        status={status.inventoryChanges}
        isEmpty={!hasGainLoss}>
        <BarChart data={bucketView}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={TICK}
            interval="preserveStartEnd"
            angle={-35}
            textAnchor="end"
            height={42}
          />
          <YAxis tick={TICK} tickFormatter={compactNumber} width={42} />
          <Tooltip
            formatter={(v: number) => [formatCurrency(v, true), t('Gain/Loss')]}
            labelStyle={TOOLTIP_STYLE}
            contentStyle={TOOLTIP_STYLE}
          />
          <ReferenceLine y={0} stroke="#A0AEC0" />
          <Bar dataKey="gainLoss" name={t('Gain/Loss')} maxBarSize={18}>
            {bucketView.map((row, i) => (
              <Cell
                key={i}
                fill={row.gainLoss >= 0 ? COLORS.green : COLORS.red}
              />
            ))}
          </Bar>
        </BarChart>
      </ChartCard>

      {/* ── Money ── */}
      <SectionHeader color="purple.400" label={t('Money')} />

      {isLoadingMoney && (
        <HStack justify="center" spacing={2}>
          <Spinner size="xs" color="purple.500" />
          <Text fontSize="2xs" color="gray.500">
            {t('Loading {{loaded}}/{{total}} snapshots…', {
              loaded: moneyLoadedCount,
              total: samplePoints.length,
            })}
          </Text>
        </HStack>
      )}

      {/* Points render as they arrive; the skeleton only covers the wait for
          the first one. A failed point shows Retry, not a silent gap. */}
      <ChartCard
        title={t('Available Money Over Time')}
        status={{
          ...moneyStatus,
          isLoading: moneyStatus.isLoading && moneyData.length === 0,
        }}
        isEmpty={moneyData.length === 0}
        onOpenDetails={moneyDrawer.onOpen}>
        <LineChart data={moneyData}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={TICK}
            interval="preserveStartEnd"
            angle={-35}
            textAnchor="end"
            height={42}
          />
          <YAxis
            tick={TICK}
            tickFormatter={compactNumber}
            width={42}
            domain={['auto', 'auto']}
          />
          <Tooltip
            formatter={(v: number) => [formatCurrency(v, true), t('Money')]}
            labelStyle={TOOLTIP_STYLE}
            contentStyle={TOOLTIP_STYLE}
          />
          <Line
            type="monotone"
            dataKey="money"
            stroke={COLORS.purple}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ChartCard>

      <ChartCard
        title={t('Money In vs Out')}
        status={status.accountTransactions}
        isEmpty={!hasTransactions}
        onOpenDetails={moneyDrawer.onOpen}
        toggle={
          <HStack spacing={1}>
            <Text fontSize="2xs" color="gray.500">
              {chartTypes.money === 'bar' ? t('Bar') : t('Line')}
            </Text>
            <Switch
              size="sm"
              colorScheme="blue"
              isChecked={chartTypes.money === 'line'}
              onChange={() => toggleChartType('money')}
            />
          </HStack>
        }>
        {chartTypes.money === 'bar' ? (
          <BarChart data={bucketView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} tickFormatter={compactNumber} width={42} />
            <Tooltip
              formatter={moneyTooltipFormatter}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar
              dataKey="moneyIn"
              name={t('Money In')}
              fill={COLORS.green}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
            <Bar
              dataKey="moneyOut"
              name={t('Money Out')}
              fill={COLORS.red}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
          </BarChart>
        ) : (
          <LineChart data={bucketView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} tickFormatter={compactNumber} width={42} />
            <Tooltip
              formatter={moneyTooltipFormatter}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line
              type="monotone"
              dataKey="moneyIn"
              name={t('Money In')}
              stroke={COLORS.green}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="moneyOut"
              name={t('Money Out')}
              stroke={COLORS.red}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        )}
      </ChartCard>

      {/* ── Payments & Debts ── */}
      <SectionHeader color="red.400" label={t('Payments & Debts')} />

      <ChartCard
        title={t('Client Debts: New vs Collected')}
        status={combineStatus(status.debts, status.accountTransactions)}
        isEmpty={!hasDebtActivity}
        onOpenDetails={clientDebtsDrawer.onOpen}
        toggle={
          <HStack spacing={1}>
            <Text fontSize="2xs" color="gray.500">
              {chartTypes.debts === 'bar' ? t('Bar') : t('Line')}
            </Text>
            <Switch
              size="sm"
              colorScheme="blue"
              isChecked={chartTypes.debts === 'line'}
              onChange={() => toggleChartType('debts')}
            />
          </HStack>
        }>
        {chartTypes.debts === 'bar' ? (
          <BarChart data={bucketView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} tickFormatter={compactNumber} width={42} />
            <Tooltip
              formatter={moneyTooltipFormatter}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar
              dataKey="newDebts"
              name={t('New Debts')}
              fill={COLORS.orange}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
            <Bar
              dataKey="collected"
              name={t('Collected')}
              fill={COLORS.green}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
          </BarChart>
        ) : (
          <LineChart data={bucketView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} tickFormatter={compactNumber} width={42} />
            <Tooltip
              formatter={moneyTooltipFormatter}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line
              type="monotone"
              dataKey="newDebts"
              name={t('New Debts')}
              stroke={COLORS.orange}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="collected"
              name={t('Collected')}
              stroke={COLORS.green}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        )}
      </ChartCard>

      <ChartCard
        title={t('Suppliers: Purchases vs Payments')}
        status={combineStatus(
          status.purchaseOrders,
          status.accountTransactions,
        )}
        isEmpty={!hasSupplierActivity}
        onOpenDetails={supplierDrawer.onOpen}
        toggle={
          <HStack spacing={1}>
            <Text fontSize="2xs" color="gray.500">
              {chartTypes.suppliers === 'bar' ? t('Bar') : t('Line')}
            </Text>
            <Switch
              size="sm"
              colorScheme="blue"
              isChecked={chartTypes.suppliers === 'line'}
              onChange={() => toggleChartType('suppliers')}
            />
          </HStack>
        }>
        {chartTypes.suppliers === 'bar' ? (
          <BarChart data={bucketView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} tickFormatter={compactNumber} width={42} />
            <Tooltip
              formatter={moneyTooltipFormatter}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar
              dataKey="purchases"
              name={t('Purchases Received')}
              fill={COLORS.blue}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
            <Bar
              dataKey="supplierPaid"
              name={t('Paid to Suppliers')}
              fill={COLORS.red}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
          </BarChart>
        ) : (
          <LineChart data={bucketView}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={TICK}
              interval="preserveStartEnd"
              angle={-35}
              textAnchor="end"
              height={42}
            />
            <YAxis tick={TICK} tickFormatter={compactNumber} width={42} />
            <Tooltip
              formatter={moneyTooltipFormatter}
              labelStyle={TOOLTIP_STYLE}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line
              type="monotone"
              dataKey="purchases"
              name={t('Purchases Received')}
              stroke={COLORS.blue}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="supplierPaid"
              name={t('Paid to Suppliers')}
              stroke={COLORS.red}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        )}
      </ChartCard>

      {/* ── Transaction Categories ── */}
      <SectionHeader color="orange.400" label={t('Transaction Categories')} />

      <ChartCard
        title={t('Net Amount by Category')}
        status={status.accountTransactions}
        isEmpty={categoryData.length === 0}
        height={Math.max(140, categoryData.length * 26 + 40)}>
        <BarChart data={categoryData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            tick={TICK}
            tickFormatter={compactNumber}
            height={20}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fontSize: 9 }}
            width={110}
          />
          <Tooltip
            formatter={(v: number) => [
              formatCurrency(v, true),
              t('Net Amount'),
            ]}
            labelStyle={TOOLTIP_STYLE}
            contentStyle={TOOLTIP_STYLE}
          />
          <ReferenceLine x={0} stroke="#A0AEC0" />
          <Bar dataKey="value" name={t('Net Amount')} maxBarSize={14}>
            {categoryData.map((row, i) => (
              <Cell key={i} fill={row.value >= 0 ? COLORS.green : COLORS.red} />
            ))}
          </Bar>
        </BarChart>
      </ChartCard>

      {/* ── Detail drawers (pre-existing history + graph views) ── */}
      <MoneyValueDrawer
        isOpen={moneyDrawer.isOpen}
        onClose={moneyDrawer.onClose}
        dateRange={dateRange}
        accountTransactions={accountTransactions}
        moneyStart={moneyStart}
        moneyEnd={moneyEnd}
      />

      <ClientDebtsDrawer
        isOpen={clientDebtsDrawer.isOpen}
        onClose={clientDebtsDrawer.onClose}
        dateRange={dateRange}
        allStoreDebts={allStoreDebts}
        knownStart={clientDebtsStart}
        knownEnd={clientDebtsEnd}
        debtPaymentsInPeriod={
          debtPaymentsPeriod
            ? debtPaymentsPeriod.money
            : (transactionBreakdown[TransactionCategory.DEBT_PAYMENT] || 0) +
              (transactionBreakdown[TransactionCategory.LOAN_PAYMENT] || 0)
        }
        refundSettledInPeriod={debtPaymentsPeriod?.refundSettled ?? 0}
      />

      <SupplierDebtsDrawer
        isOpen={supplierDrawer.isOpen}
        onClose={supplierDrawer.onClose}
        dateRange={dateRange}
        supplierValuation={supplierValuation}
        purchaseOrderPayments={purchaseOrderPayments}
        suppliersMap={suppliersMap}
      />
    </VStack>
  );
}
